import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader, AdminField } from "./shared";

export default function GuestsTab() {
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null); // { ...customer } or null

    const refresh = () => api.get("/admin/users").then((r) => setUsers((r.data || []).filter((u) => u.role === "customer")));
    useEffect(() => { refresh(); }, []);

    const save = async () => {
        const u = editing;
        if (!u.name?.trim()) { toast.error("Name is required"); return; }
        if (!u.email?.trim()) { toast.error("Email is required"); return; }
        try {
            await api.patch(`/admin/users/${u.user_id}`, {
                name: u.name, email: u.email, phone: u.phone || "", active: u.active,
            });
            toast.success("Guest updated");
            setEditing(null);
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save guest");
        }
    };

    return (
        <div data-testid="admin-guests">
            <PageHeader eyebrow="Admin · Guests" title="Everyone in the book." subtitle="Customers who have registered or booked. Staff and owners live in the Employees tab." />
            <div className="border border-espresso/15">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-widest2 text-espresso bg-bone2/50">
                        <tr>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Phone</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Joined</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted2" data-testid="admin-guests-empty">No guests yet.</td></tr>}
                        {users.map((u) => (
                            <tr key={u.user_id} className="border-t border-espresso/10" data-testid={`guest-row-${u.user_id}`}>
                                <td className="px-4 py-4 font-medium">{u.name}</td>
                                <td className="px-4 py-4">{u.email}</td>
                                <td className="px-4 py-4 text-muted2">{u.phone || "—"}</td>
                                <td className="px-4 py-4">
                                    <span className={`px-2 py-1 text-[10px] uppercase tracking-widest2 ${u.active === false ? "bg-terracotta/15 text-terracotta" : "bg-forest/10 text-forest"}`}>
                                        {u.active === false ? "Disabled" : "Active"}
                                    </span>
                                </td>
                                <td className="px-4 py-4 text-muted2 text-xs">{u.created_at?.slice(0, 10)}</td>
                                <td className="px-4 py-4 text-right">
                                    <button onClick={() => setEditing({ ...u, active: u.active !== false })} data-testid={`guest-edit-${u.user_id}`} className="text-xs uppercase tracking-widest2 text-forest hover:text-terracotta">Edit</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <Dialog open onOpenChange={() => setEditing(null)}>
                    <DialogContent className="max-w-lg" data-testid="guest-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-3xl">Edit guest</DialogTitle>
                            <DialogDescription>Update this guest's contact details or disable their account to block sign-in.</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <AdminField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="guest-form-name" />
                            <AdminField label="Phone" value={editing.phone || ""} onChange={(v) => setEditing({ ...editing, phone: v })} testid="guest-form-phone" />
                            <div className="md:col-span-2">
                                <label className="eyebrow block mb-2">Email</label>
                                <input
                                    type="email" value={editing.email}
                                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                                    data-testid="guest-form-email"
                                    className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent focus:border-forest outline-none"
                                />
                            </div>
                        </div>
                        <label className="flex items-center gap-3 text-sm mt-4">
                            <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="guest-form-active" />
                            Account active (can sign in)
                        </label>
                        <DialogFooter className="mt-6">
                            <button onClick={() => setEditing(null)} className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2">Cancel</button>
                            <button onClick={save} data-testid="guest-form-save" className="px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold">Save guest</button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
